import type { FormInstance } from 'rsuite/Form'
import type { BrandInfoState } from './BrandFormState'

import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react'
import { useFetchBrandInfo } from '@@partner:graphql/query/brand/currentBrandInfo.gql'
import { useUpdateCurrentBrandInfo } from '@@partner:graphql/mutation/brand/updateCurrentBrandInfo.gql'
import { useFetchBrandShops } from '@@partner:graphql/query/order/brandShops.gql'
import { makeBrandFormState, mapStateToApiVariable } from './BrandFormState'
import { mergeLeftOnly, mergeLeft } from 'merge-left-utils'
import {
  makeGraphQLErrorNotification,
  notifyOk,
  notifyError
} from '@@partner:helpers/notification'

import { BrandFormValidationModel } from './validation/BrandFormValidationModel'

import { Form, Grid, Row, Col, Panel, Toggle, Button, IconButton, Input, Modal } from 'rsuite'
import AppLoader from '@components/common/AppLoader'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import DropzonePreview from '@components/common/form/DropzonePreview'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import FormElementErrorDisplay from '@components/common/form/FormElementErrorDisplay'
import FormColorPicker from '@components/common/form/FormColorPicker'
import FormSelect from '@components/common/form/FormSelect'
import { SaveButton } from '@components/common/form/Buttons'
import TrashIcon from '@rsuite/icons/Trash'
import FileDownloadIcon from '@rsuite/icons/FileDownload'

import { API_URL_UPLOAD_BRAND_LOGO, API_URL_UPLOAD_BRAND_FILE } from '@@partner:config/api'
import { useSetBrandFile } from '@@partner:graphql/mutation/brand/setBrandFile.gql'
import { useRemoveBrandFile } from '@@partner:graphql/mutation/brand/removeBrandFile.gql'
import { SHOP } from '@hive/status'

interface AppStyleConfig {
  light: Record<string, string>
  dark: Record<string, string>
}

const DEFAULT_APP_STYLE_CONFIG: AppStyleConfig = {
  light: {
    brandPrimary: '#e84e29',
    brandSecondary: '#FFC002',
    brandSuccess: '#4caf50',
    brandDanger: '#d9534f',
    brandWarning: '#f0ad4e',
    brandSales: '#FFDD05',
    textSales: '#121212',
    brandInfo: '#62B1F6',
    brandDark: '#121212',
    brandLight: '#a9a9a9',
    brandGrey: '#8E8E93',
    brandBackgroundColor: '#f9f9f9',
    brandBackgroundGrey: '#f3f3f3',
    brandBackgroundColorSemi: '#f2f2f2',
    textColor: '#434242',
    invertedTextColor: '#fff',
    iconColor: '#333',
    rateStartColor: '#EF6E1A',
    navigationTintColor: '#333',
    priceColor: '#bdbdbd',
    addToCartButtonColor: '#ff5200',
    addToCartButtonTextColor: '#fff',
    primaryBackground: '#e5f0ff',
    alertDanger: '#ffe8e8',
    alertWarning: '#ffd99e',
    textNoteColor: '#808080',
    buttonDisableBg: '#b5b5b5',
    backgroundColor: '#fff',
    listItemBorderColor: '#c9c9c9',
    separatorBackgroundColor: '#F0EFF5',
    separatorBorderColor: '#dbdbdb',
    bagsIcon: '#2b913a',
    termobagsIcon: '#0018d0',
    barcodeLineColor: '#000'
  },
  dark: {
    brandPrimary: '#e84e29',
    brandSecondary: '#FFC002',
    brandSuccess: '#74AE48',
    brandDanger: '#e06861',
    brandWarning: '#f0ad4e',
    brandSales: '#FFDD05',
    textSales: '#000',
    brandInfo: '#326592',
    brandDark: '#fff',
    brandLight: '#3a3a3a',
    brandGrey: '#8E8E93',
    brandBackgroundColor: '#141414',
    brandBackgroundGrey: '#121212',
    brandBackgroundColorSemi: '#1a1a1a',
    textColor: '#e9e9e9',
    invertedTextColor: '#000',
    iconColor: '#fff',
    rateStartColor: '#EF6E1A',
    navigationTintColor: '#e9e9e9',
    priceColor: '#bdbdbd',
    addToCartButtonColor: '#ff5200',
    addToCartButtonTextColor: '#fff',
    primaryBackground: '#e5f0ff',
    alertDanger: '#6e2121',
    alertWarning: '#ab7013',
    textNoteColor: '#c7c7c7',
    buttonDisableBg: '#b5b5b5',
    backgroundColor: '#121212',
    listItemBorderColor: '#5e5e5e',
    separatorBackgroundColor: '#F0EFF5',
    separatorBorderColor: '#dbdbdb',
    bagsIcon: '#2b913a',
    termobagsIcon: '#0018d0',
    barcodeLineColor: '#000'
  }
}

const COLOR_LABELS: Record<string, string> = {
  brandPrimary: 'Основной цвет бренда',
  brandSecondary: 'Вторичный цвет бренда',
  brandSuccess: 'Цвет успеха',
  brandDanger: 'Цвет опасности',
  brandWarning: 'Цвет предупреждения',
  brandSales: 'Цвет акций',
  textSales: 'Текст акций',
  brandInfo: 'Цвет информации',
  brandDark: 'Темный цвет',
  brandLight: 'Светлый цвет',
  brandGrey: 'Серый цвет',
  brandBackgroundColor: 'Фон приложения',
  brandBackgroundGrey: 'Серый фон',
  brandBackgroundColorSemi: 'Полупрозрачный фон',
  textColor: 'Цвет текста',
  invertedTextColor: 'Инвертированный текст',
  iconColor: 'Цвет иконок',
  rateStartColor: 'Цвет звезд рейтинга',
  navigationTintColor: 'Цвет навигации',
  priceColor: 'Цвет цены',
  addToCartButtonColor: 'Фон кнопки "В корзину"',
  addToCartButtonTextColor: 'Текст кнопки "В корзину"',
  primaryBackground: 'Основной фон',
  alertDanger: 'Фон предупреждения (опасность)',
  alertWarning: 'Фон предупреждения',
  textNoteColor: 'Цвет примечаний',
  buttonDisableBg: 'Фон отключенной кнопки',
  backgroundColor: 'Цвет фона',
  listItemBorderColor: 'Граница элемента списка',
  separatorBackgroundColor: 'Фон разделителя',
  separatorBorderColor: 'Граница разделителя',
  bagsIcon: 'Иконка сумок',
  termobagsIcon: 'Иконка термосумок',
  barcodeLineColor: 'Цвет линии штрихкода'
}

export interface BrandFormProps {
  slug: string
}

export default function BrandForm ({ slug }: BrandFormProps) {
  const [ state, setState ] = useState<BrandInfoState>(() => makeBrandFormState())
  const [ styleConfig, setStyleConfig ] = useState<AppStyleConfig>(DEFAULT_APP_STYLE_CONFIG)
  const [ isDarkTheme, setIsDarkTheme ] = useState(false)

  // Brand file upload state
  const [ showFileModal, setShowFileModal ] = useState(false)
  const [ newFileKey, setNewFileKey ] = useState('')
  const [ newFileName, setNewFileName ] = useState('')
  const [ uploadedFileId, setUploadedFileId ] = useState<number | null>(null)

  const [
    updateBrandInfo,
    { loading: loadingUpdateBrand, error: updateBrandError }
  ] = useUpdateCurrentBrandInfo()

  const [ setBrandFile, { loading: loadingSetBrandFile } ] = useSetBrandFile()
  const [ removeBrandFile, { loading: loadingRemoveBrandFile } ] = useRemoveBrandFile()

  const {
    data,
    loading: loadingBrand,
    error: errorLoadingBrand
  } = useFetchBrandInfo()

  const { data: brandShopsData, loading: loadingBrandShops } =
    useFetchBrandShops()

  const shopsList = useMemo(
    () =>
      (brandShopsData?.shops ?? [])
        .filter((shop) => shop.status === SHOP.ENABLED)
        .map((shop) => ({
          value: Number(shop.id),
          label: `${shop.name} ${shop.address}`
        })),
    [ brandShopsData?.shops ]
  )

  const [ formError, setFormError ] = useState({})
  const formRef = useRef<FormInstance>()

  // set initial from backend
  useEffect(() => {
    if (!loadingBrand && data) {
      const brandInfo = data?.brandInfo
      setState((state) => mergeLeft(state, makeBrandFormState(brandInfo)))
      
      // Parse appStyleConfig
      if (brandInfo?.appStyleConfig) {
        try {
          const parsed = JSON.parse(brandInfo.appStyleConfig)
          setStyleConfig(mergeLeft(DEFAULT_APP_STYLE_CONFIG, parsed))
        }
        catch (e) {
          console.error('Failed to parse appStyleConfig:', e)
          setStyleConfig(DEFAULT_APP_STYLE_CONFIG)
        }
      }
    }
  }, [ data, loadingBrand ])

  // run form checks on state change
  useEffect(() => {
    formRef.current?.check()
  }, [ state, formRef ])

  // set ref to form
  const onSetRef = useCallback((ref: FormInstance) => {
    formRef.current = ref
  }, [])

  const handleFormSubmit = useCallback(
    (isOk = false, d) => {
      if (isOk) {
        // Serialize styleConfig to JSON string
        const stateWithConfig = {
          ...state,
          appStyleConfig: JSON.stringify(styleConfig)
        }
        
        updateBrandInfo({
          variables: {
            brandInfo: mapStateToApiVariable(stateWithConfig)
          }
        })
          .then(({ data }) => {
            notifyOk('Бренд обновлён')
            setState((state) => mergeLeft(state, data?.brandInfo))
          })
          .catch(makeGraphQLErrorNotification('Ошибка сохранения данных'))
      }
      else {
        notifyError('Форма заполнена некорректно')
      }
    },
    [ state, styleConfig, updateBrandInfo ]
  )

  const handleFormChange = useCallback((formData: any) => {
    setState((state) => mergeLeft(state, formData))
  }, [])

  const handleFormError = useCallback((error: any) => {
    setFormError(error)
  }, [])

  const handleBrandLogoUploaded = useCallback((logo_id, url) => {
    setState((state) => {
      return mergeLeftOnly([ 'logo_id', 'logo', 'url' ], state, {
        logo_id,
        logo: { url }
      })
    })
  }, [])

  const handleUnlinkLogoClick = useCallback(() => {
    setState((state) =>
      mergeLeftOnly([ 'logo_id', 'logo', 'url' ], state, makeBrandFormState())
    )
  }, [])

  const handleSquareLogoUploaded = useCallback((square_logo_id, url) => {
    setState((state) => {
      return mergeLeftOnly([ 'square_logo_id', 'squareLogo', 'url' ], state, {
        square_logo_id,
        squareLogo: { url }
      })
    })
  }, [])

  const handleUnlinkSquareLogoClick = useCallback(() => {
    setState((state) =>
      mergeLeftOnly([ 'square_logo_id', 'squareLogo', 'url' ], state, makeBrandFormState())
    )
  }, [])

  // Brand file handlers
  const handleBrandFileUploaded = useCallback((fileId: number, _url: string) => {
    setUploadedFileId(fileId)
  }, [])

  const handleOpenFileModal = useCallback(() => {
    setShowFileModal(true)
    setNewFileKey('')
    setNewFileName('')
    setUploadedFileId(null)
  }, [])

  const handleCloseFileModal = useCallback(() => {
    setShowFileModal(false)
    setNewFileKey('')
    setNewFileName('')
    setUploadedFileId(null)
  }, [])

  const handleSaveBrandFile = useCallback(() => {
    if (!uploadedFileId || !newFileKey) {
      notifyError('Загрузите файл и укажите ключ')
      return
    }

    setBrandFile({
      variables: {
        key: newFileKey,
        file_id: uploadedFileId,
        name: newFileName || newFileKey
      }
    })
      .then(() => {
        notifyOk('Файл добавлен')
        handleCloseFileModal()
      })
      .catch(makeGraphQLErrorNotification('Ошибка добавления файла'))
  }, [ uploadedFileId, newFileKey, newFileName, setBrandFile, handleCloseFileModal ])

  const handleRemoveBrandFile = useCallback((file_id: number, name: string) => {
    if (!confirm(`Удалить файл "${name}"?`)) return

    removeBrandFile({
      variables: { file_id }
    })
      .then(() => notifyOk('Файл удалён'))
      .catch(makeGraphQLErrorNotification('Ошибка удаления файла'))
  }, [ removeBrandFile ])

  const handleColorChange = useCallback((colorKey: string, value: string) => {
    setStyleConfig((config) => {
      const theme = isDarkTheme ? 'dark' : 'light'
      return {
        ...config,
        [theme]: {
          ...config[theme],
          [colorKey]: value
        }
      }
    })
  }, [ isDarkTheme ])

  const currentTheme = isDarkTheme ? 'dark' : 'light'
  const currentColors = styleConfig[currentTheme]

  if (loadingBrand) {
    return <AppLoader content={`Загрузка данных бренда #${slug}`} />
  }

  if (errorLoadingBrand) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Не удалось загрузить данные бренда #${slug} для редактирования`}
        error={errorLoadingBrand}
      />
    )
  }

  return (
    <Form
      layout="horizontal"
      model={BrandFormValidationModel}
      formValue={state}
      onChange={handleFormChange}
      onSubmit={handleFormSubmit}
      onCheck={handleFormError}
      ref={onSetRef}
    >
      {updateBrandError && (
        <GraphQLCommonErrorDisplay error={updateBrandError} />
      )}

      <Grid fluid>
        <Row>
          <Col xs={24} md={12}>
            <Panel header="Основные настройки" bordered shaded>
              <Form.Group>
                <Form.ControlLabel>Название</Form.ControlLabel>
                <Form.Control name="name" placeholder="Название" />
              </Form.Group>

              <Form.Group>
                <Form.ControlLabel>Имя для URL</Form.ControlLabel>
                <Form.Control name="slug" placeholder="Имя для URL" />
              </Form.Group>

              <Form.Group>
                <Form.ControlLabel>Магазин по умолчанию</Form.ControlLabel>

                {loadingBrandShops || loadingBrand ? (
                  <AppLoader.Inline />
                ) : (
                  <FormSelect name="selectedDefaultShopId" data={shopsList} />
                )}
              </Form.Group>

              {/* <Form.Group>
                <Form.ControlLabel>Цвет фона</Form.ControlLabel>
                <FormColorPicker name="primaryColorHex" inputClassName="h-12 p-1" />
                <Form.HelpText>(HEX: {state.primaryColorHex})</Form.HelpText>
              </Form.Group>

              <Form.Group>
                <Form.ControlLabel>Цвет текста</Form.ControlLabel>
                <FormColorPicker
                  name="primaryTextColorHex"
                  inputClassName="w-32 h-12 p-1"
                />

                <Form.HelpText>(HEX: {state.primaryTextColorHex})</Form.HelpText>
              </Form.Group>
 */}
              <Row className="my-4 items-center justify-center flex">
                <Col sm={state.logo?.url ? 18 : 24}>
                  <Form.Group>
                    <Form.ControlLabel>Логотип</Form.ControlLabel>
                    <FormElementErrorDisplay field="logo_id" error={formError} />

                    <DropzoneUploader
                      action={API_URL_UPLOAD_BRAND_LOGO}
                      name="brand-logo"
                      className="w-full"
                      onUploaded={handleBrandLogoUploaded}
                      multiple={false}
                    />
                  </Form.Group>
                </Col>
                {state.logo?.url && (
                  <Col sm={6}>
                    <DropzonePreview
                      url={state.logo.url}
                      onDelete={handleUnlinkLogoClick}
                    />
                  </Col>
                )}
              </Row>

              <Row className="my-4 items-center justify-center flex">
                <Col sm={state.squareLogo?.url ? 18 : 24}>
                  <Form.Group>
                    <Form.ControlLabel>Квадратный логотип</Form.ControlLabel>
                    <FormElementErrorDisplay field="square_logo_id" error={formError} />

                    <DropzoneUploader
                      action={API_URL_UPLOAD_BRAND_LOGO}
                      name="brand-logo"
                      className="w-full"
                      onUploaded={handleSquareLogoUploaded}
                      multiple={false}
                    />
                  </Form.Group>
                </Col>
                {state.squareLogo?.url && (
                  <Col sm={6}>
                    <DropzonePreview
                      url={state.squareLogo.url}
                      onDelete={handleUnlinkSquareLogoClick}
                    />
                  </Col>
                )}
              </Row>
            </Panel>

            {/* Brand Files Section */}
            <Panel header="Файлы бренда" bordered shaded className="mt-4">
              <div className="mb-4">
                <Button appearance="primary" onClick={handleOpenFileModal}>
                  Добавить файл
                </Button>
              </div>

              {data?.brandInfo?.brandFiles && data.brandInfo.brandFiles.length > 0 ? (
                <div className="space-y-2">
                  {data.brandInfo.brandFiles.map((file) => (
                    <div
                      key={file.key}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded border"
                    >
                      <div className="flex-1">
                        <div className="font-medium">{file.name}</div>
                        <div className="text-sm text-gray-500">
                          Ключ: <code className="bg-gray-200 px-1 rounded">{file.key}</code>
                          {file.fileSize && (
                            <span className="ml-2">
                              Размер: {(file.fileSize / 1024).toFixed(1)} KB
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          Добавлен: {new Date(file.addedAt).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {file.file?.url && (
                          <IconButton
                            icon={<FileDownloadIcon />}
                            appearance="subtle"
                            color="blue"
                            size="sm"
                            as="a"
                            href={file.file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          />
                        )}
                        <IconButton
                          icon={<TrashIcon />}
                          appearance="subtle"
                          color="red"
                          size="sm"
                          loading={loadingRemoveBrandFile}
                          onClick={() => handleRemoveBrandFile(Number(file.file_id), file.name)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-center py-4">
                  Файлы не добавлены
                </div>
              )}
            </Panel>
          </Col>

          <Col xs={24} md={12}>
            <Panel header="Конфигурация стилей приложения" bordered shaded>
              <div className="mb-4 flex items-center gap-4">
                <span>Светлая тема</span>
                <Toggle 
                  checked={isDarkTheme} 
                  onChange={setIsDarkTheme}
                  size="lg"
                />
                <span>Темная тема</span>
              </div>

              <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                {Object.entries(currentColors).map(([ key, value ]) => (
                  <div key={key} className="mb-3">
                    <Form.ControlLabel>{COLOR_LABELS[key] || key}</Form.ControlLabel>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={value}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="h-10 w-20 cursor-pointer rounded border"
                      />
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => handleColorChange(key, e.target.value)}
                        className="flex-1 h-10 px-3 rounded border"
                        placeholder="#000000"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <Form.Group className="mt-4">
                <Form.ControlLabel>JSON для просмотра</Form.ControlLabel>
                <Form.Control
                  name="appStyleConfigPreview"
                  accepter="textarea"
                  rows={6}
                  value={JSON.stringify(styleConfig, null, 2)}
                  readOnly
                />
                <Form.HelpText>Автоматически генерируется из настроек выше</Form.HelpText>
              </Form.Group>
            </Panel>
          </Col>
        </Row>
      </Grid>

      <SaveButton loading={loadingUpdateBrand} disabled={loadingUpdateBrand}>
        Cохранить изменения
      </SaveButton>

      {/* File Upload Modal */}
      <Modal open={showFileModal} onClose={handleCloseFileModal} size="sm">
        <Modal.Header>
          <Modal.Title>Добавить файл бренда</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <label className="block mb-1 font-medium">Ключ файла *</label>
              <Input
                value={newFileKey}
                onChange={setNewFileKey}
                placeholder="Например: loyalty_terms"
              />
              <div className="text-xs text-gray-500 mt-1">
                Только латиница, цифры и нижние подчёркивания
              </div>
            </div>

            <div>
              <label className="block mb-1 font-medium">Название файла</label>
              <Input
                value={newFileName}
                onChange={setNewFileName}
                placeholder="Например: Условия программы лояльности"
              />
            </div>

            <div>
              <label className="block mb-1 font-medium">Файл *</label>
              <DropzoneUploader
                action={API_URL_UPLOAD_BRAND_FILE}
                name="brand-file"
                className="w-full"
                onUploaded={handleBrandFileUploaded}
                multiple={false}
              >
                <div className="text-gray-500 py-4">
                  {uploadedFileId ? (
                    <span className="text-green-600">✓ Файл загружен (ID: {uploadedFileId})</span>
                  ) : (
                    <span>Нажмите или перенесите файл (PDF, PNG, JPG)</span>
                  )}
                </div>
              </DropzoneUploader>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleCloseFileModal} appearance="subtle">
            Отмена
          </Button>
          <Button
            onClick={handleSaveBrandFile}
            appearance="primary"
            loading={loadingSetBrandFile}
            disabled={!uploadedFileId || !newFileKey}
          >
            Сохранить
          </Button>
        </Modal.Footer>
      </Modal>
    </Form>
  )
}
